import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { logAudit } from '../auditLog';
import { buscarCantones, cantonPorCodigo } from '../lib/pe/pe-cantones';
import {
  CAMPOS_SEDE, FORM_VACIO, accionQuitar, avisoCentros, avisoSumaDeclarados, formDesdeSede, mensajeErrorSede, mensajeQuitar,
  ordenarSedes, perfilDesdeDeclarado, registradosPorSede, sedesActivas, sincronizaMatrizConPerfil, validarSede,
  type FilaSede, type FormSede, type UsoSede,
} from '../lib/sedes';
import type { RiskLevel } from '../lib/sst-rules';

// F1a (M40): Empresa → Sedes. Catálogo de centros de trabajo para asignar y filtrar; el Checklist sigue por empresa (K1).
// Los cálculos por sede (delegado o comité, monitor, SP-02, PO-16 a 18 y un plan por sede) son F1b.

interface Perfil { workers_count: number | null; work_centers_count: number | null; risk_level: RiskLevel | null }
type SiteId = { site_id: string | null };

export default function SedesPage({ onNavigateToSSOPerfil }: { onNavigateToSSOPerfil?: () => void } = {}) {
  const { company, profile } = useCompany();
  const { impersonated } = useImpersonation();
  const companyId = (impersonated ?? company)?.id ?? '';
  const rol = profile?.role;
  const puedeEditar = rol === 'admin' || rol === 'manager' || rol === 'superadmin' || !!impersonated;
  const puedeQuitar = rol === 'admin' || rol === 'superadmin' || !!impersonated;

  const [sedes, setSedes] = useState<FilaSede[]>([]);
  const [trabajadores, setTrabajadores] = useState<{ site_id: string | null; status: string | null }[]>([]);
  const [bodegas, setBodegas] = useState<{ site_id: string | null }[]>([]);
  const [planSede, setPlanSede] = useState<string | null>(null);
  // F3a (M41): inspecciones, programaciones y hallazgos también impiden borrar la sede (el trigger los cuenta).
  const [inspecciones, setInspecciones] = useState<SiteId[]>([]);
  const [programaciones, setProgramaciones] = useState<SiteId[]>([]);
  const [hallazgos, setHallazgos] = useState<SiteId[]>([]);
  const [perfil, setPerfil] = useState<Perfil | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [verInactivas, setVerInactivas] = useState(false);

  const [editando, setEditando] = useState<FilaSede | null | undefined>(undefined); // undefined = cerrado; null = nueva
  const [quitando, setQuitando] = useState<FilaSede | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(async () => {
    if (!companyId) return;
    setCargando(true); setError('');
    const [s, w, h, p, sso, ins, prog, hal] = await Promise.all([
      supabase.from('company_sites').select(CAMPOS_SEDE).eq('company_id', companyId),
      supabase.from('workers').select('site_id, status').eq('company_id', companyId),
      supabase.from('warehouses').select('site_id').eq('company_id', companyId),
      supabase.from('emergency_plans').select('site_id').eq('company_id', companyId).maybeSingle(),
      supabase.from('company_sso_profile').select('workers_count, work_centers_count, risk_level').eq('company_id', companyId).maybeSingle(),
      supabase.from('insp_inspections').select('site_id').eq('company_id', companyId),
      supabase.from('insp_schedules').select('site_id').eq('company_id', companyId),
      supabase.from('insp_findings').select('site_id').eq('company_id', companyId),
    ]);
    if (s.error) setError(mensajeErrorSede(s.error));
    setSedes(ordenarSedes((s.data ?? []) as unknown as FilaSede[]));
    setTrabajadores((w.data ?? []) as { site_id: string | null; status: string | null }[]);
    setBodegas((h.data ?? []) as { site_id: string | null }[]);
    setPlanSede((p.data as { site_id?: string | null } | null)?.site_id ?? null);
    setPerfil((sso.data ?? null) as Perfil | null);
    setInspecciones((ins.data ?? []) as SiteId[]);
    setProgramaciones((prog.data ?? []) as SiteId[]);
    setHallazgos((hal.data ?? []) as SiteId[]);
    setCargando(false);
  }, [companyId]);

  useEffect(() => { cargar(); }, [cargar]);

  const registrados = useMemo(() => registradosPorSede(trabajadores), [trabajadores]);
  const uso = useCallback((id: string): UsoSede => ({
    trabajadores: trabajadores.filter(w => w.site_id === id).length,
    bodegas: bodegas.filter(b => b.site_id === id).length,
    plan: planSede === id,
    inspecciones: inspecciones.filter(i => i.site_id === id).length,
    programaciones: programaciones.filter(i => i.site_id === id).length,
    hallazgos: hallazgos.filter(i => i.site_id === id).length,
  }), [trabajadores, bodegas, planSede, inspecciones, programaciones, hallazgos]);

  const activas = sedesActivas(sedes);
  const visibles = verInactivas ? sedes : activas;
  const inactivas = sedes.length - activas.length;
  const avisoSuma = avisoSumaDeclarados(sedes, perfil?.workers_count);
  const centros = avisoCentros(perfil?.work_centers_count, sedes);

  async function guardar(datos: ReturnType<typeof validarSede>['datos'], sede: FilaSede | null) {
    if (!datos) return;
    setOcupado(true); setError(''); setOk('');
    const fallo = (texto: string) => { setError(texto); setOcupado(false); };
    if (sede) {
      const { error: e } = await supabase.from('company_sites').update({ ...datos, updated_by: profile?.id ?? null }).eq('id', sede.id);
      if (e) return fallo(mensajeErrorSede(e));
      logAudit({ company_id: companyId, action: 'updated', entity_type: 'company_site', entity_id: sede.id, entity_name: datos.name });
      // Con una sola sede activa, el declarado de la Matriz es el del Perfil SSO (el otro sentido lo hace la base).
      if (sincronizaMatrizConPerfil(sede, sedes, datos.workers_declared, perfil)) {
        const { error: e2 } = await supabase.from('company_sso_profile')
          .update({ ...perfilDesdeDeclarado(datos.workers_declared as number, perfil?.risk_level), updated_at: new Date().toISOString() })
          .eq('company_id', companyId);
        if (e2) { setEditando(undefined); await cargar(); return fallo('La sede se guardó, pero no se pudo actualizar el Perfil SSO: ' + mensajeErrorSede(e2)); }
      }
      setOk('Sede actualizada.');
    } else {
      const { data, error: e } = await supabase.from('company_sites')
        .insert({ ...datos, company_id: companyId, site_type: 'sucursal', is_main: false, created_by: profile?.id ?? null, updated_by: profile?.id ?? null })
        .select('id').single();
      if (e) return fallo(mensajeErrorSede(e));
      logAudit({ company_id: companyId, action: 'created', entity_type: 'company_site', entity_id: (data as { id: string }).id, entity_name: datos.name });
      setOk('Sede creada.');
    }
    setEditando(undefined);
    await cargar();
    setOcupado(false);
  }

  async function quitar(sede: FilaSede) {
    const accion = accionQuitar(sede, uso(sede.id));
    if (accion === 'ninguna') return;
    setOcupado(true); setError(''); setOk('');
    const { error: e } = accion === 'borrar'
      ? await supabase.from('company_sites').delete().eq('id', sede.id)
      : await supabase.from('company_sites').update({ active: false, updated_by: profile?.id ?? null }).eq('id', sede.id);
    setOcupado(false); setQuitando(null);
    if (e) { setError(mensajeErrorSede(e)); return; }
    logAudit({ company_id: companyId, action: accion === 'borrar' ? 'deleted' : 'archived', entity_type: 'company_site', entity_id: sede.id, entity_name: sede.name });
    setOk(accion === 'borrar' ? `Sede "${sede.name}" borrada.` : `Sede "${sede.name}" desactivada.`);
    await cargar();
  }

  async function reactivar(sede: FilaSede) {
    setOcupado(true); setError(''); setOk('');
    const { error: e } = await supabase.from('company_sites').update({ active: true, updated_by: profile?.id ?? null }).eq('id', sede.id);
    setOcupado(false);
    if (e) { setError(mensajeErrorSede(e)); return; }
    logAudit({ company_id: companyId, action: 'restored', entity_type: 'company_site', entity_id: sede.id, entity_name: sede.name });
    setOk(`Sede "${sede.name}" reactivada.`);
    await cargar();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>Sedes</h2>
          <p style={{ color: '#64748b', fontSize: '0.82rem', margin: '4px 0 0' }}>
            {activas.length} sede{activas.length !== 1 ? 's' : ''} activa{activas.length !== 1 ? 's' : ''}
            {inactivas > 0 && ` · ${inactivas} inactiva${inactivas !== 1 ? 's' : ''}`}
          </p>
        </div>
        {puedeEditar && (
          <button type="button" style={primaryBtn} onClick={() => { setOk(''); setError(''); setEditando(null); }}>+ Agregar sede</button>
        )}
      </div>

      <p style={{ color: '#94a3b8', fontSize: '0.82rem', margin: 0 }}>
        Los centros de trabajo de tu empresa. Con una sola sede, StorSafe se ve igual que siempre; con dos o más, Trabajadores
        y Bodegas te dejan elegir la sede de cada uno y filtrar por sede. El Checklist sigue siendo de toda la empresa.
      </p>

      {error && <div role="alert" style={{ ...aviso, ...avisoRojo }}>{error}</div>}
      {ok && <div role="status" style={{ ...aviso, ...avisoVerde }}>{ok}</div>}

      {centros && (
        <div role="status" aria-label="Centros de trabajo declarados y sedes registradas" style={{ ...aviso, ...(centros.difiere ? avisoAmbar : avisoAzul) }}>
          {centros.texto}
          {centros.difiere && ' El número de centros declarado en tu Perfil SSO no cambia solo: revísalo si ya no corresponde.'}
          {centros.difiere && onNavigateToSSOPerfil && <>{' '}<button type="button" style={linkBtn} onClick={onNavigateToSSOPerfil}>Ir al Perfil SSO</button></>}
        </div>
      )}
      {avisoSuma && (
        <div role="status" aria-label="Suma de los declarados por sede" style={{ ...aviso, ...avisoAmbar }}>
          {avisoSuma} Ajusta el número declarado de cada sede o el de tu Perfil SSO.
        </div>
      )}

      {inactivas > 0 && (
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#94a3b8', fontSize: '0.84rem', cursor: 'pointer', minHeight: 40 }}>
          <input type="checkbox" checked={verInactivas} onChange={e => setVerInactivas(e.target.checked)} />
          Mostrar inactivas ({inactivas})
        </label>
      )}

      {cargando ? (
        <div style={{ ...tarjeta, color: '#475569', textAlign: 'center' }}>Cargando…</div>
      ) : visibles.length === 0 ? (
        <div style={{ ...tarjeta, color: '#475569', textAlign: 'center' }}>Aún no hay sedes registradas.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))', gap: 12 }}>
          {visibles.map(s => {
            const c = cantonPorCodigo(s.canton_code);
            const u = uso(s.id);
            return (
              <div key={s.id} data-testid={`sede-${s.id}`} style={{ ...tarjeta, opacity: s.active ? 1 : 0.6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <strong style={{ color: '#e2e8f0', fontSize: '0.95rem', overflowWrap: 'anywhere' }}>{s.name}</strong>
                  {s.is_main
                    ? <span style={{ ...pastilla, color: '#2FBF71', borderColor: 'rgba(47,191,113,0.4)' }}>Matriz</span>
                    : <span style={{ ...pastilla, color: '#7dd3fc', borderColor: 'rgba(56,189,248,0.4)' }}>Sucursal</span>}
                  {!s.active && <span style={{ ...pastilla, color: '#94a3b8', borderColor: '#475569' }}>Inactiva</span>}
                </div>
                <p style={{ color: '#94a3b8', fontSize: '0.8rem', margin: '6px 0 0', overflowWrap: 'anywhere' }}>
                  {[s.address, c ? `${c.canton} (${c.provincia})` : null, s.parish].filter(Boolean).join(' · ') || 'Sin dirección ni cantón'}
                </p>
                <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', margin: '10px 0 0', fontSize: '0.8rem' }}>
                  <dt style={dt}>Declarados</dt><dd style={dd}>{s.workers_declared ?? '—'}</dd>
                  <dt style={dt}>Registrados</dt><dd style={dd}>{registrados.get(s.id) ?? 0} <span style={{ color: '#64748b' }}>(activos y en licencia)</span></dd>
                  <dt style={dt}>Bodegas</dt><dd style={dd}>{u.bodegas}</dd>
                  {u.plan && <><dt style={dt}>Plan</dt><dd style={dd}>Plan de emergencias de la empresa</dd></>}
                  {s.responsible_name && <><dt style={dt}>Responsable</dt><dd style={dd}>{s.responsible_name}</dd></>}
                  {s.phone && <><dt style={dt}>Teléfono</dt><dd style={dd}>{s.phone}</dd></>}
                </dl>
                {(puedeEditar || puedeQuitar) && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                    {puedeEditar && s.active && (
                      <button type="button" style={secondaryBtn} disabled={ocupado} onClick={() => { setOk(''); setError(''); setEditando(s); }}>Editar</button>
                    )}
                    {puedeQuitar && !s.is_main && s.active && (
                      <button type="button" style={dangerBtn} disabled={ocupado} onClick={() => { setOk(''); setError(''); setQuitando(s); }}>
                        {accionQuitar(s, u) === 'borrar' ? 'Borrar' : 'Desactivar'}
                      </button>
                    )}
                    {puedeQuitar && !s.active && (
                      <button type="button" style={secondaryBtn} disabled={ocupado} onClick={() => reactivar(s)}>Reactivar</button>
                    )}
                  </div>
                )}
                {s.is_main && <p style={{ color: '#475569', fontSize: '0.72rem', margin: '8px 0 0' }}>La Matriz no se puede borrar ni desactivar.</p>}
              </div>
            );
          })}
        </div>
      )}

      {editando !== undefined && (
        <ModalSede
          sede={editando}
          otrosNombres={sedes.filter(x => x.id !== editando?.id).map(x => x.name)}
          unaSola={activas.length === 1}
          ocupado={ocupado}
          onCancelar={() => setEditando(undefined)}
          onGuardar={d => guardar(d, editando)}
        />
      )}
      {quitando && (
        <ModalConfirmarSede
          titulo={accionQuitar(quitando, uso(quitando.id)) === 'borrar' ? 'Borrar sede' : 'Desactivar sede'}
          mensaje={mensajeQuitar(quitando.name, uso(quitando.id))}
          textoConfirmar={accionQuitar(quitando, uso(quitando.id)) === 'borrar' ? 'Borrar' : 'Desactivar'}
          ocupado={ocupado}
          onCancelar={() => setQuitando(null)}
          onConfirmar={() => quitar(quitando)}
        />
      )}
    </div>
  );
}

// ── Formulario de la sede ─────────────────────────────────────────────────────

function ModalSede({ sede, otrosNombres, unaSola, ocupado, onCancelar, onGuardar }: {
  sede: FilaSede | null; otrosNombres: string[]; unaSola: boolean; ocupado: boolean;
  onCancelar: () => void; onGuardar: (d: ReturnType<typeof validarSede>['datos']) => void;
}) {
  const [f, setF] = useState<FormSede>(sede ? formDesdeSede(sede) : { ...FORM_VACIO });
  const [errores, setErrores] = useState<Partial<Record<keyof FormSede, string>>>({});
  const [busqueda, setBusqueda] = useState('');
  const resultados = busqueda.trim().length >= 2 ? buscarCantones(busqueda, 8) : [];
  const canton = cantonPorCodigo(f.canton_code);
  const primero = useRef<HTMLInputElement>(null);
  useEffect(() => {
    primero.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onCancelar]);

  const campo = (k: keyof FormSede) => ({
    value: (f[k] ?? '') as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => setF(x => ({ ...x, [k]: e.target.value })),
    disabled: ocupado, style: input,
  });
  function enviar(e: React.FormEvent) {
    e.preventDefault();
    const r = validarSede(f, otrosNombres);
    setErrores(r.errores);
    if (r.datos) onGuardar(r.datos);
  }

  return (
    <div style={fondo} onClick={e => e.target === e.currentTarget && onCancelar()}>
      <form style={modal} role="dialog" aria-modal="true" aria-labelledby="sede-titulo" onSubmit={enviar}>
        <h3 id="sede-titulo" style={{ color: '#f1f5f9', fontSize: '1.05rem', margin: 0 }}>
          {sede ? `Editar ${sede.is_main ? 'Matriz' : 'sede'}` : 'Agregar sede'}
        </h3>
        <Campo etiqueta="Nombre *" error={errores.name}><input ref={primero} {...campo('name')} placeholder="Ej: Sucursal Guayaquil" /></Campo>
        {/* F1a2: el tipo se ve en el formulario. Matriz es solo la principal (la crea la base) y no se elige para otra. */}
        <Campo etiqueta="Tipo">
          <select style={input} aria-label="Tipo" value={sede?.is_main ? 'matriz' : 'sucursal'} disabled={ocupado || !!sede?.is_main} onChange={() => undefined}>
            <option value="sucursal" disabled={!!sede?.is_main}>Sucursal</option>
            <option value="matriz" disabled={!sede?.is_main}>Matriz (solo la sede principal)</option>
          </select>
          <small style={{ color: '#64748b', fontSize: '0.72rem' }}>
            {sede?.is_main ? 'Es la sede principal de la empresa; su tipo no cambia.' : 'La Matriz es la sede principal y ya existe; las demás sedes son sucursales.'}
          </small>
        </Campo>
        <Campo etiqueta="Dirección" error={errores.address}><input {...campo('address')} placeholder="Calle, número e intersección" /></Campo>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={etiqueta}>Cantón</span>
          {canton && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', color: '#e2e8f0', fontSize: '0.84rem' }}>
              <span><b>{canton.canton}</b> ({canton.provincia}) · INEC {canton.codigo}</span>
              <button type="button" style={linkBtn} disabled={ocupado} onClick={() => setF(x => ({ ...x, canton_code: null }))}>Quitar</button>
            </div>
          )}
          <input style={input} disabled={ocupado} aria-label="Buscar cantón" value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder={canton ? 'Buscar otro cantón…' : 'Escribe el cantón o la provincia…'} />
          {resultados.length > 0 && (
            <div role="listbox" style={{ display: 'flex', flexDirection: 'column', border: '1px solid #334155', borderRadius: 8, maxHeight: 200, overflowY: 'auto' }}>
              {resultados.map(c => (
                <button key={c.codigo} type="button" role="option" aria-selected={f.canton_code === c.codigo} style={opcion}
                  onClick={() => { setF(x => ({ ...x, canton_code: c.codigo })); setBusqueda(''); }}>
                  {c.canton} <small style={{ color: '#64748b' }}>({c.provincia})</small>
                </button>
              ))}
            </div>
          )}
          <small style={{ color: '#64748b', fontSize: '0.72rem' }}>Lista del INEC (Clasificador Geográfico Estadístico 2026), la misma del Plan de Emergencias.</small>
          {errores.canton_code && <span style={errorTxt}>{errores.canton_code}</span>}
        </div>
        <Campo etiqueta="Parroquia" error={errores.parish}><input {...campo('parish')} /></Campo>
        <div style={fila2}>
          <Campo etiqueta="Latitud (opcional)" error={errores.latitude}><input {...campo('latitude')} inputMode="decimal" placeholder="-0,1807" /></Campo>
          <Campo etiqueta="Longitud (opcional)" error={errores.longitude}><input {...campo('longitude')} inputMode="decimal" placeholder="-78,4678" /></Campo>
        </div>
        <div style={fila2}>
          <Campo etiqueta="Teléfono" error={errores.phone}><input {...campo('phone')} inputMode="tel" /></Campo>
          <Campo etiqueta="Responsable" error={errores.responsible_name}><input {...campo('responsible_name')} /></Campo>
        </div>
        <div style={fila2}>
          <Campo etiqueta="Distancia al poblado más cercano (km)" error={errores.distance_to_nearest_town_km}>
            <input {...campo('distance_to_nearest_town_km')} inputMode="decimal" />
          </Campo>
          <Campo etiqueta="Trabajadores declarados" error={errores.workers_declared}>
            <input {...campo('workers_declared')} inputMode="numeric" />
          </Campo>
        </div>
        {sede?.is_main && unaSola && (
          <p style={{ color: '#7dd3fc', fontSize: '0.78rem', margin: 0 }}>
            Con una sola sede, los trabajadores declarados de la Matriz son los de tu Perfil SSO: si cambias el número aquí, también cambia allí.
          </p>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" style={secondaryBtn} onClick={onCancelar} disabled={ocupado}>Cancelar</button>
          <button type="submit" style={primaryBtn} disabled={ocupado}>{ocupado ? 'Guardando…' : sede ? 'Guardar cambios' : 'Agregar sede'}</button>
        </div>
      </form>
    </div>
  );
}

// Modal propio de la app (sin confirm()): foco en Cancelar y Escape cierra.
function ModalConfirmarSede({ titulo, mensaje, textoConfirmar, ocupado, onCancelar, onConfirmar }: {
  titulo: string; mensaje: string; textoConfirmar: string; ocupado: boolean; onCancelar: () => void; onConfirmar: () => void;
}) {
  const cancelar = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    cancelar.current?.focus();
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onCancelar]);
  return (
    <div style={fondo} onClick={e => e.target === e.currentTarget && onCancelar()}>
      <div style={{ ...modal, maxWidth: 460 }} role="dialog" aria-modal="true" aria-labelledby="sede-quitar-titulo">
        <h3 id="sede-quitar-titulo" style={{ color: '#f1f5f9', fontSize: '1.05rem', margin: 0 }}>{titulo}</h3>
        <p style={{ color: '#cbd5e1', fontSize: '0.86rem', margin: 0 }}>{mensaje}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
          <button ref={cancelar} type="button" style={secondaryBtn} onClick={onCancelar} disabled={ocupado}>Cancelar</button>
          <button type="button" style={dangerBtn} onClick={onConfirmar} disabled={ocupado}>{ocupado ? 'Guardando…' : textoConfirmar}</button>
        </div>
      </div>
    </div>
  );
}

function Campo({ etiqueta: texto, error, children }: { etiqueta: string; error?: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
      <span style={etiqueta}>{texto}</span>
      {children}
      {error && <span style={errorTxt}>{error}</span>}
    </label>
  );
}

// ── Estilos (los de Trabajadores) ─────────────────────────────────────────────

const tarjeta: React.CSSProperties = { background: '#1e293b', border: '1px solid #334155', borderRadius: 14, padding: '16px 18px' };
const aviso: React.CSSProperties = { padding: '12px 16px', borderRadius: 10, fontSize: '0.84rem', overflowWrap: 'anywhere' };
const avisoAzul: React.CSSProperties = { background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.3)', color: '#7dd3fc' };
const avisoAmbar: React.CSSProperties = { background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', color: '#fbbf24' };
const avisoRojo: React.CSSProperties = { background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' };
const avisoVerde: React.CSSProperties = { background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)', color: '#86efac' };
const pastilla: React.CSSProperties = { border: '1px solid', borderRadius: 6, padding: '1px 8px', fontSize: '0.7rem', fontWeight: 700 };
const dt: React.CSSProperties = { color: '#64748b' };
const dd: React.CSSProperties = { color: '#e2e8f0', margin: 0 };
const primaryBtn: React.CSSProperties = {
  padding: '9px 20px', minHeight: 40, background: 'linear-gradient(135deg, #1D6FA3 0%, #2FBF71 100%)',
  border: 'none', color: '#fff', fontWeight: 700, fontSize: '0.88rem', borderRadius: 8, cursor: 'pointer',
};
const secondaryBtn: React.CSSProperties = {
  padding: '8px 16px', minHeight: 40, background: 'transparent', border: '1px solid #334155',
  color: '#94a3b8', fontWeight: 600, fontSize: '0.84rem', borderRadius: 8, cursor: 'pointer',
};
const dangerBtn: React.CSSProperties = { ...secondaryBtn, border: '1px solid rgba(239,68,68,0.4)', color: '#f87171' };
const linkBtn: React.CSSProperties = {
  background: 'none', border: 'none', padding: 0, color: 'inherit', fontWeight: 700, fontSize: 'inherit', textDecoration: 'underline', cursor: 'pointer',
};
const fondo: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
  zIndex: 400, padding: '24px 16px', overflowY: 'auto',
};
const modal: React.CSSProperties = {
  background: '#1e293b', border: '1px solid #334155', borderRadius: 16, width: '100%', maxWidth: 580, padding: '20px 24px',
  display: 'flex', flexDirection: 'column', gap: 14, margin: 'auto 0', boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
};
const etiqueta: React.CSSProperties = { color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600 };
const errorTxt: React.CSSProperties = { color: '#f87171', fontSize: '0.74rem' };
const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px', minHeight: 40, background: 'rgba(255,255,255,0.04)', border: '1px solid #334155',
  borderRadius: 8, color: '#e2e8f0', fontSize: '16px', outline: 'none', boxSizing: 'border-box',
};
const opcion: React.CSSProperties = {
  textAlign: 'left', padding: '8px 12px', minHeight: 40, background: 'transparent', border: 'none', borderBottom: '1px solid #1e293b',
  color: '#e2e8f0', fontSize: '0.84rem', cursor: 'pointer',
};
const fila2: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 12 };
